import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Table, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Input,
  Select,
  Statistic,
  Avatar,
  Tooltip,
  message,
  Alert
} from 'antd';
import { 
  SearchOutlined, 
  ExclamationCircleOutlined, 
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeOutlined,
  WarningOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getCompliancePending } from '../../api';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const CompliancePending = () => {
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchComplianceIssues();
  }, []);

  useEffect(() => {
    filterIssues();
  }, [complianceIssues, searchTerm, priorityFilter]);

  const fetchComplianceIssues = async () => {
    setLoading(true);
    try {
      const response = await getCompliancePending();
      setComplianceIssues(response.data || []);
    } catch (error) {
      console.error('Error fetching compliance issues:', error);
      message.error('Failed to fetch compliance pending items');
    } finally {
      setLoading(false);
    }
  };

  const filterIssues = () => {
    let filtered = [...complianceIssues];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(issue =>
        issue.employee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.job?.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.job?.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status (using priorityFilter for status filtering)
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(issue => issue.status === priorityFilter);
    }

    setFilteredIssues(filtered);
  };

  const handleViewDetails = (issue) => {
    navigate(`/agency/compliance-details/${issue._id}`, { 
      state: { issue } 
    });
  };



  const columns = [
    {
      title: 'Healthcare Professional',
      key: 'employee',
      render: (record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size="small" 
            icon={<UserOutlined />} 
            style={{ marginRight: '8px', backgroundColor: '#006971' }}
          />
          <div>
            <div style={{ fontWeight: '500' }}>
              {record.employee || 'N/A'}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.employee}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Assignment',
      key: 'assignment',
      render: (record) => (
        <div>
          <div style={{ fontWeight: '500', marginBottom: '2px' }}>
            {record.job?.title || 'N/A'}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.job?.code || 'No Code'} • {record.job?.department || 'General'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Compliance Issue',
      key: 'compliance',
      render: (record) => (
        <div>
          <Tag color="orange">
            DOCUMENT VERIFICATION
          </Tag>
          <div style={{ marginTop: '4px' }}>
            <Text style={{ fontSize: '12px' }}>
              Awaiting compliance document verification
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EnvironmentOutlined style={{ marginRight: '4px', color: '#666' }} />
            <Text style={{ fontSize: '13px' }}>
              {record.job?.address?.city || 'N/A'}, {record.job?.address?.state || 'N/A'}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Start Date',
      key: 'startDate',
      render: (record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CalendarOutlined style={{ marginRight: '4px', color: '#666' }} />
            <Text style={{ fontSize: '13px' }}>
              {record.startDate ? new Date(record.startDate).toLocaleDateString() : 'N/A'}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record) => {
        const getStatusColor = (status) => {
          const colors = {
            'compliance-in-progress': 'orange',
            'compliance-pending': 'red',
            'compliance-approved': 'green',
            'active': 'green'
          };
          return colors[status] || 'orange';
        };
        
        return (
          <Tag color={getStatusColor(record.status)}>
            {record.status === 'active' 
              ? 'ACTIVE' 
              : record.status?.replace('-', ' ').toUpperCase() || 'PENDING'
            }
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
              style={{ color: '#006971' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const stats = [
    {
      title: 'Total Items',
      value: filteredIssues.length,
      color: '#006971'
    },
    {
      title: 'In Progress',
      value: filteredIssues.filter(i => i.status === 'compliance-in-progress').length,
      color: '#faad14'
    },
    {
      title: 'Active',
      value: filteredIssues.filter(i => i.status === 'active').length,
      color: '#52c41a'
    },
    {
      title: 'Starting Soon',
      value: filteredIssues.filter(i => {
        if (!i.startDate) return false;
        const startDate = new Date(i.startDate);
        const now = new Date();
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return startDate >= now && startDate <= weekFromNow;
      }).length,
      color: '#ff4d4f'
    }
  ];

  const urgentCount = filteredIssues.filter(i => {
    if (!i.startDate) return false;
    const startDate = new Date(i.startDate);
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    return startDate >= now && startDate <= threeDaysFromNow;
  }).length;
  const inProgressCount = filteredIssues.filter(i => i.status === 'compliance-in-progress').length;

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#006971' }}>
          Compliance Management
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Monitor and manage compliance status for your healthcare professional assignments
        </Text>
      </div>

      {/* Alert for critical issues */}
      {(urgentCount > 0 || inProgressCount > 0) && (
        <Alert
          message="Attention Required"
          description={`You have ${inProgressCount} assignments in compliance progress and ${urgentCount} assignments starting within 3 days that need immediate attention.`}
          type="warning"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
          showIcon
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card style={{ textAlign: 'center', borderRadius: '8px' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: '24px', fontWeight: '600' }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search compliance issues..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={priorityFilter}
              onChange={setPriorityFilter}
              style={{ width: '100%', borderRadius: '6px' }}
            >
              <Option value="all">All Status</Option>
              <Option value="compliance-in-progress">In Progress</Option>
              <Option value="compliance-pending">Pending</Option>
              <Option value="compliance-approved">Approved</Option>
              <Option value="active">Active</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchComplianceIssues}
                style={{ borderRadius: '6px' }}
              >
                Refresh
              </Button>
              <Button 
                icon={<ExportOutlined />} 
                style={{ borderRadius: '6px' }}
              >
                Export
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Compliance Issues Table */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={filteredIssues}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} compliance issues`,
          }}
          scroll={{ x: 1200 }}
          rowClassName={(record) => {
            const isUrgent = record.startDate && new Date(record.startDate) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
            const isInProgress = record.status === 'compliance-in-progress';
            if (isUrgent) return 'compliance-urgent';
            if (isInProgress) return 'compliance-in-progress';
            return '';
          }}
        />
      </Card>

      <style jsx>{`
        .compliance-urgent {
          background-color: #fff2f0 !important;
        }
        .compliance-in-progress {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  );
};

export default CompliancePending; 